import { useEffect, useState } from "react";
import { X, Trash2 } from "lucide-react";
import { useLang } from "@/lib/adminLang";
import { STAGES } from "./budsjettUtils";
import { PlanEditor } from "./PlanEditor";
import { PakkeVelger } from "@/pages/admin/eventos/PakkeVelger";

export default function SponsorModal({ sponsor, members, settings, onSave, onDelete, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: sponsor?.name || "",
    packageName: sponsor?.packageName || "",
    value: sponsor?.value ?? 0,
    stage: sponsor?.stage || "researching",
    probabilityOverride: sponsor?.probabilityOverride ?? "",
    ownerId: sponsor?.ownerId || "",
    paymentPlan: sponsor?.paymentPlan || [],
    notes: sponsor?.notes || "",
    nextStep: sponsor?.nextStep || "",
    nextStepDue: sponsor?.nextStepDue || "",
  });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const stdProb = (settings?.stageProbabilities || {})[form.stage];

  const save = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        value: Number(form.value) || 0,
        probabilityOverride: form.probabilityOverride === "" ? null : Number(form.probabilityOverride),
        ownerId: form.ownerId || null,
        nextStepDue: form.nextStepDue || null,
        note: note.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="sponsor-modal">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onClose} />
      <aside className="sak-drawer relative w-full max-w-[520px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
          <h2 className="text-[15px] font-semibold">{sponsor ? t("Edit sponsor") : t("New sponsor")}</h2>
          <div className="flex items-center gap-1">
            {onDelete && (
              <button type="button" onClick={onDelete} className="p-1.5 text-neutral-400 hover:text-red-600 transition-colors" data-testid="sponsor-delete-btn">
                <Trash2 size={14} />
              </button>
            )}
            <button type="button" onClick={onClose} className="p-1.5 text-neutral-400 hover:text-neutral-800 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="grid grid-cols-2 gap-3">
          <label className="adm-label">
            {t("Name")}
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className="adm-input" data-testid="sponsor-name-input" />
          </label>
          <PakkeVelger label={t("Package")} verdi={form.packageName} onVelg={(v) => set("packageName", v)} testid="sponsor-package-input" />
          <label className="adm-label">
            {t("Value")} ({t("Price ex VAT").split(" ").slice(-2).join(" ")})
            <input type="number" value={form.value} onChange={(e) => set("value", e.target.value)} className="adm-input" data-testid="sponsor-value-input" />
          </label>
          <label className="adm-label">
            {t("Stage")}
            <select value={form.stage} onChange={(e) => set("stage", e.target.value)} className="adm-input" data-testid="sponsor-modal-stage">
              {STAGES.map((st) => (
                <option key={st.k} value={st.k}>{t(st.label)}</option>
              ))}
            </select>
          </label>
          <label className="adm-label">
            {t("Probability (override)")}
            <input
              type="number"
              min={0}
              max={100}
              placeholder={stdProb != null ? `${t("Default:")} ${stdProb}%` : ""}
              value={form.probabilityOverride}
              onChange={(e) => set("probabilityOverride", e.target.value)}
              className="adm-input"
              data-testid="sponsor-prob-input"
            />
          </label>
          <label className="adm-label">
            {t("Owner")}
            <select value={form.ownerId} onChange={(e) => set("ownerId", e.target.value)} className="adm-input">
              <option value="">{t("None")}</option>
              {(members || []).map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </label>
          <label className="adm-label">
            {t("Next step")}
            <input value={form.nextStep} onChange={(e) => set("nextStep", e.target.value)} placeholder="—" className="adm-input" data-testid="sponsor-nextstep-input" />
          </label>
          <label className="adm-label">
            {t("Due date")}
            <input type="date" value={form.nextStepDue} onChange={(e) => set("nextStepDue", e.target.value)} className="adm-input" data-testid="sponsor-nextstepdue-input" />
          </label>
          <label className="adm-label col-span-2">
            {t("Notes")}
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="adm-input" />
          </label>
        </div>
        <p className="adm-label mt-4 mb-2">{t("Expected payment (empty = event month)")}</p>
        <PlanEditor plan={form.paymentPlan} onChange={(p) => set("paymentPlan", p)} testid="sponsor-plan-editor" />
        {sponsor && (
          <label className="adm-label mt-4">
            {t("Log a note (added to the timeline)")}
            <input value={note} onChange={(e) => setNote(e.target.value)} className="adm-input" data-testid="sponsor-note-input" />
          </label>
        )}
        {sponsor && (sponsor.activity || []).length > 0 && (
          <div className="mt-5 pt-4 border-t border-[#F1EEE7]" data-testid="sponsor-timeline">
            <p className="adm-label mb-1">{t("Activity")}</p>
            <div>
              {[...sponsor.activity].reverse().slice(0, 12).map((a, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-t border-[#F1EEE7] first:border-t-0" data-testid={`sponsor-akt-${i}`}>
                  <span className="bud-mono text-[9px] uppercase tracking-[0.08em] text-neutral-400 shrink-0 mt-[3px] w-14">
                    {a.at ? new Date(a.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
                  </span>
                  <p className="text-[11.5px] text-neutral-500 leading-relaxed min-w-0">
                    <span className="text-neutral-800 font-medium">{a.actor}</span> · {a.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
        <div className="shrink-0 px-6 py-3 border-t border-neutral-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="adm-btn-ghost">{t("Cancel")}</button>
          <button type="button" onClick={save} disabled={!form.name.trim() || saving} className="adm-btn" data-testid="sponsor-save-btn">
            {saving ? t("Saving …") : t("Save")}
          </button>
        </div>
      </aside>
    </div>
  );
}
